'use client'

import { useEffect } from 'react'

// This replaces the root layout when it fires, so it renders its own <html>
// and <body> and can't rely on app/globals.css or Tailwind having loaded.
// Inline styles only, no imports beyond React, on purpose. A mistake here
// produces a blank page instead of a degraded one.

// Signature Next/webpack use for a stale-chunk fetch after a redeploy moves
// the content hashes out from under a tab that's still open. Kept in sync
// with the same check in app/(dashboard)/error.tsx, app/(admin)/error.tsx,
// and app/(auth)/error.tsx.
const CHUNK_ERROR_PATTERN = /Loading chunk|Loading CSS chunk|error loading dynamically imported module|Failed to fetch dynamically imported module/i
// Renamed from the old bare-timestamp key so a tab still holding that value
// can't get JSON-parsed as a number and read as a truthy, wrong-shaped guard.
const CHUNK_RELOAD_KEY = 'halo-core:chunk-reload'
// Attempt counter, not a recency window: bounds automatic reloads absolutely
// regardless of how long each cycle takes to fail again. A genuine skew
// incident gets up to two automatic tries; a third failure means it isn't
// skew and the boundary renders instead of looping.
const CHUNK_RELOAD_MAX_ATTEMPTS = 2
// A fresh incident (a later, unrelated deploy) resets the counter instead of
// inheriting an old one. Keeping the original `t` on every write, not this
// constant, is what keeps the window from sliding forward on each reload.
const CHUNK_RELOAD_RESET_MS = 60000

function isChunkLoadError(error: Error): boolean {
  return error.name === 'ChunkLoadError' || CHUNK_ERROR_PATTERN.test(error.message ?? '')
}

interface ChunkReloadGuard { n: number; t: number }

// sessionStorage throws in a sandboxed iframe without allow-same-origin, and
// in some privacy configurations. `null` means storage itself is
// unavailable (fail closed, caller must not auto-reload); it is never
// returned for a merely missing or malformed value, those are a fresh
// incident, not a failure.
function readGuard(): ChunkReloadGuard | null {
  let raw: string | null
  try {
    raw = window.sessionStorage.getItem(CHUNK_RELOAD_KEY)
  } catch {
    return null
  }
  if (!raw) return { n: 0, t: Date.now() }
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && typeof parsed.n === 'number' && typeof parsed.t === 'number') {
      return parsed as ChunkReloadGuard
    }
  } catch {
    // Falls through to the fresh default below.
  }
  return { n: 0, t: Date.now() }
}

function writeGuard(value: ChunkReloadGuard): boolean {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

function clearGuard(): void {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    // Best-effort. A throw here must not take out the Try again button.
  }
}

// The --halo-* tokens from globals.css, hardcoded since Tailwind classes don't
// apply here. The theme script and its `.dark` class are gone with the root
// layout, so light-dark() follows the OS scheme instead of the stored choice.
// A browser without light-dark() drops the declaration and shows its defaults.
const COLORS = {
  bg: 'light-dark(#F2F3ED, #10140F)',
  card: 'light-dark(#FCFDF8, #171D15)',
  inset: 'light-dark(#E9ECE2, #0C100B)',
  border: 'light-dark(#D5DACB, #333D2D)',
  controlEdge: 'light-dark(#7D8772, #6A795E)',
  text: 'light-dark(#20251D, #EEF2E8)',
  textMuted: 'light-dark(#5B6455, #BBC5B1)',
  textFaint: 'light-dark(#626C5A, #A2AF97)',
  accent: 'light-dark(#D2F16F, #D5F486)',
  onAccent: '#182011',
  danger: 'light-dark(#AB3939, #F19994)',
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    if (!isChunkLoadError(error)) return
    const guard = readGuard()
    if (guard === null) return // storage unavailable, fail closed, no auto-reload
    const fresh = Date.now() - guard.t > CHUNK_RELOAD_RESET_MS
    const current = fresh ? { n: 0, t: Date.now() } : guard
    if (current.n >= CHUNK_RELOAD_MAX_ATTEMPTS) return // suppressed, boundary renders
    // Write before reload, never after: reload tears down the page, so
    // anything written after it never executes.
    if (writeGuard({ n: current.n + 1, t: current.t })) {
      window.location.reload()
    }
  }, [error])

  const handleReset = () => {
    clearGuard()
    reset()
  }

  return (
    <html lang="en" style={{ colorScheme: 'light dark' }}>
      <body style={{ margin: 0, background: COLORS.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: '24px' }}>
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center', background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 3, padding: 24 }}>
          <h2 style={{ fontSize: 18, lineHeight: '26px', fontWeight: 500, color: COLORS.text, margin: 0 }}>Something broke</h2>
          <p style={{ fontSize: 14, color: COLORS.textMuted, marginTop: 8, marginBottom: 16 }}>
            Try again, or reload the page if it keeps happening.
          </p>

          {error.digest && (
            <div style={{ textAlign: 'left', marginBottom: 12 }}>
              <p style={{ fontSize: 12, lineHeight: '18px', fontWeight: 500, color: COLORS.textFaint, margin: '0 0 4px' }}>Error code</p>
              <code style={{ display: 'block', padding: '6px 10px', borderRadius: 2, background: COLORS.inset, color: COLORS.textMuted, fontSize: 12, wordBreak: 'break-all', userSelect: 'all' }}>
                {error.digest}
              </code>
            </div>
          )}

          {process.env.NODE_ENV !== 'production' && error.message && (
            <div style={{ textAlign: 'left', marginBottom: 16 }}>
              <p style={{ fontSize: 12, lineHeight: '18px', fontWeight: 500, color: COLORS.textFaint, margin: '0 0 4px' }}>Message (dev only)</p>
              <code style={{ display: 'block', padding: '6px 10px', borderRadius: 2, background: COLORS.inset, color: COLORS.danger, fontSize: 12, wordBreak: 'break-all' }}>
                {error.message}
              </code>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 4 }}>
            <button
              onClick={handleReset}
              style={{ height: 36, padding: '0 16px', borderRadius: 2, background: COLORS.accent, color: COLORS.onAccent, fontSize: 14, fontWeight: 500, border: 'none', cursor: 'pointer' }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ height: 36, padding: '0 16px', borderRadius: 3, background: COLORS.card, color: COLORS.text, fontSize: 14, fontWeight: 500, border: `1px solid ${COLORS.controlEdge}`, cursor: 'pointer' }}
            >
              Reload page
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}
